import apiClient from '../../../../../shared/lib/api'
import type { StoryboardFrameData } from './types'

export type StoryboardStatus = 'draft' | 'ready' | 'deleted'

export interface StoryboardBrief {
  productName?: string
  targetAudience?: string
  mainBenefit?: string
  painPoints?: string[]
  tone?: string
  callToAction?: string
  keyMessages?: string[]
  videoLength?: string
  platform?: string
  // Brand & Product Bible context
  brandName?: string
  valueProposition?: string
  productBenefits?: string[]
  proofPoints?: string[]
  // Buyer Persona from Product Bible
  personaName?: string
  personaAge?: string
  personaProblem?: string
  personaEmotions?: string[]
  personaAngles?: string[]
}

export interface StoryboardSettingsData {
  name?: string
  aspectRatio?: string
  duration?: string
  targetPlatform?: string
  style?: string
  audioTrack?: string
  language?: string
}

export interface StoryboardAsset {
  id: string
  tenantId: string
  name: string
  description?: string
  status: StoryboardStatus
  frames: StoryboardFrameData[]
  sourceFrames?: StoryboardFrameData[]
  brief?: StoryboardBrief
  settings?: StoryboardSettingsData
  sourceVideoKey?: string
  geminiJobId?: string
  createdAt: string
  updatedAt: string
  deletedAt?: string
}

export interface ListStoryboardsResponse {
  storyboards: StoryboardAsset[]
  nextCursor?: string
}

export interface GetStoryboardResponse {
  storyboard: StoryboardAsset
}

export interface CreateStoryboardInput {
  name: string
  description?: string
  frames?: StoryboardFrameData[]
  sourceFrames?: StoryboardFrameData[]
  brief?: StoryboardBrief
  settings?: StoryboardSettingsData
  sourceVideoKey?: string
  geminiJobId?: string
}

export interface UpdateStoryboardInput {
  name?: string
  description?: string
  status?: StoryboardStatus
  frames?: StoryboardFrameData[]
  sourceFrames?: StoryboardFrameData[]
  brief?: StoryboardBrief
  settings?: StoryboardSettingsData
  sourceVideoKey?: string
  geminiJobId?: string
}

export async function listStoryboards(params?: {
  limit?: number
  cursor?: string
  q?: string
}): Promise<ListStoryboardsResponse> {
  const res = await apiClient.get<ListStoryboardsResponse>('/storyboards', { params })
  return res.data
}

export async function createStoryboard(input: CreateStoryboardInput): Promise<StoryboardAsset> {
  const res = await apiClient.post<{ storyboard: StoryboardAsset }>('/storyboards', input)
  return res.data.storyboard
}

export async function getStoryboard(id: string): Promise<StoryboardAsset> {
  const res = await apiClient.get<GetStoryboardResponse>(`/storyboards/${id}`)
  return res.data.storyboard
}

export async function updateStoryboard(id: string, input: UpdateStoryboardInput): Promise<StoryboardAsset> {
  const res = await apiClient.put<{ storyboard: StoryboardAsset }>(`/storyboards/${id}`, input)
  return res.data.storyboard
}

export async function deleteStoryboard(id: string): Promise<void> {
  await apiClient.delete(`/storyboards/${id}`)
}

// Storyboard Assembly Types
export interface SceneMatchResult {
  sceneNumber: number
  contentRole: string
  videoId?: string
  videoTitle?: string
  videoS3Key?: string
  matchScore: number
  matched: boolean
  clipStartSec: number
  clipEndSec: number
  clipDuration: number
}

export interface StoryboardAssemblyRequest {
  storyboard: {
    scenes: Array<{
      sceneNumber: number
      startTime: number
      endTime: number
      description: string
      contentRole: string
      visualType: string
      audio: string
      textOverlay: string
      mood: string
      keyElements: string[]
      voice?: string
      pointOfView?: string
      suggestedDuration?: number
      // Pre-assigned video ID (from user selection or auto-fill)
      videoId?: string
      // Clip trim times in seconds
      clipStartSec?: number
      clipEndSec?: number
    }>
    meta: {
      durationSec: number
      scenesCount: number
      brand?: string
      product?: string
    }
  }
  outputFormat?: 'mp4' | 'webm'
  aspectRatio?: '9:16' | '16:9' | '1:1' | '4:5'
  includeAudio?: boolean
}

export interface StoryboardAssemblyResponse {
  success: boolean
  jobId: string
  videoUrl?: string
  downloadUrl?: string
  s3Key?: string
  durationSec?: number
  sceneMatches: SceneMatchResult[]
  status: 'pending' | 'running' | 'completed' | 'failed'
  errorMessage?: string
}

/** Start an async storyboard assembly job. Returns immediately with a jobId. */
export async function assembleStoryboard(
  request: StoryboardAssemblyRequest
): Promise<StoryboardAssemblyResponse> {
  const res = await apiClient.post<StoryboardAssemblyResponse>(
    '/api/storyboard/assemble',
    request,
    { timeout: 15000 }
  )
  return res.data
}

/** Poll the status of a storyboard assembly job. */
export async function getAssemblyJobStatus(jobId: string): Promise<StoryboardAssemblyResponse> {
  const res = await apiClient.get<StoryboardAssemblyResponse>(
    `/api/storyboard/assemble/status/${jobId}`,
    { timeout: 10000 }
  )
  return res.data
}
